import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Rocket,
  Award,
  Flame,
  BookOpenCheck,
  LayoutGrid,
  PenTool,
} from "lucide-react";
import { motion, useInView, useAnimation } from "framer-motion";

const features = [
  { icon: Rocket, label: "Just Launched", link: "/justlaunched-books" },
  { icon: Award, label: "Best Reads", link: "/bestreads-books" },
  { icon: Flame, label: "Trending Books", link: "/trending-books" },
  { icon: BookOpenCheck, label: "Top Rated", link: "/top-rated-books" },
  { icon: LayoutGrid, label: "Browse Categories", link: "/explore-books" },
  { icon: PenTool, label: "Browse Authors", link: "/explore-authors" },
];

// ─── Feature Card ──────────────────────────────────────────────────────────────
const FeatureCard = ({ icon: Icon, label, link }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-20px" });
  const controls = useAnimation();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isInView) {
      controls.start({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          duration: 0.4,
          ease: [0.25, 0.1, 0.25, 1],
          delay: Math.random() * 0.2,
        },
      });
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={controls}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
      // Each card is one‐third of the viewport width on mobile
      className="snap-center flex-shrink-0 basis-1/3"
    >
     <Link
  to={link}
  className="rounded-md"
>

        <div
          className="flex flex-col items-center mx-2"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="relative">
            <div
              className={`
                bg-primary rounded-full
                w-16 h-16 sm:w-20 sm:h-20
                flex items-center justify-center
                shadow-lg transition-all duration-300
                ${isHovered ? "border-2 border-primary" : ""}
              `}
            >
              <Icon
                className={`
                  text-white
                  w-6 h-6 sm:w-8 sm:h-8
                  transition-transform duration-300
                `}
                style={{
                  transform: isHovered ? "scale(1.15)" : "scale(1)",
                }}
              />
            </div>

            {/* Hover “pulse” overlay */}
            {isHovered && (
              <motion.div
                className="absolute inset-0 rounded-full bg-primary/20"
                initial={{ scale: 0.8, opacity: 0.4 }}
                animate={{ scale: 1.2, opacity: 0 }}
                transition={{ duration: 0.4 }}
              />
            )}
          </div>

                <span className="mt-3 font-medium text-xs sm:text-sm text-gray-700 text-center leading-tight whitespace-nowrap">
           {label}
         </span>
        </div>
      </Link>
    </motion.div>
  );
};

// ─── CategoryFeatures ─────────────────────────────────────────────────────────
const CategoryFeatures = ({ className = "" }) => {
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect “is mobile” (<640px)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto‐scroll effect runs only on mobile
  useEffect(() => {
    if (!isMobile || !containerRef.current) return;
  
    const scrollContainer = containerRef.current;
    const cardWidth = scrollContainer.firstChild?.getBoundingClientRect().width || 0;
    let autoScrollEnabled = true;
    let direction = 1;
  
    let scrollInterval;
  
    const scrollToNext = () => {
      if (!autoScrollEnabled || !scrollContainer) return;
  
      const currentScroll = scrollContainer.scrollLeft;
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
  
      if (currentScroll >= maxScroll - cardWidth) direction = -1;
      if (currentScroll <= 0) direction = 1;
  
      const nextScroll = currentScroll + direction * cardWidth;
      scrollContainer.scrollTo({ left: nextScroll, behavior: "smooth" });
    };
  
    scrollInterval = setInterval(scrollToNext, 2500); // Adjust interval time
  
    const pauseScroll = () => {
      autoScrollEnabled = false;
      clearInterval(scrollInterval);
    };
  
    const resumeScroll = () => {
      autoScrollEnabled = true;
      scrollInterval = setInterval(scrollToNext, 2500);
    };
  
    scrollContainer.addEventListener("mousedown", pauseScroll);
    scrollContainer.addEventListener("touchstart", pauseScroll);
    scrollContainer.addEventListener("mouseup", resumeScroll);
    scrollContainer.addEventListener("touchend", resumeScroll);
    scrollContainer.addEventListener("mouseleave", resumeScroll);
  
    return () => {
      clearInterval(scrollInterval);
      scrollContainer.removeEventListener("mousedown", pauseScroll);
      scrollContainer.removeEventListener("touchstart", pauseScroll);
      scrollContainer.removeEventListener("mouseup", resumeScroll);
      scrollContainer.removeEventListener("touchend", resumeScroll);
      scrollContainer.removeEventListener("mouseleave", resumeScroll);
    };
  }, [isMobile]);
  

  return (
    <div className={`relative overflow-hidden py-2 sm:-mt-4 mt-4 ${className}`}>
      {/* ─── Mobile only: Left/right fade overlays ─────────────────────────── */}
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none sm:hidden" />
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none sm:hidden" />

      <div className="max-w-7xl mx-auto px-2">
        {/* ─── MOBILE (show 3 cards at once, auto‐scroll) ─────────────── */}
        <div
         ref={containerRef}
         className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory py-2 px-1 sm:hidden"
         style={{
           scrollBehavior: "smooth", // <--- ensures smooth transition
           WebkitOverflowScrolling: "touch",
           scrollSnapType: "x mandatory",
         }}
        >
          {features.map((item, idx) => (
            <FeatureCard
              key={idx}
              icon={item.icon}
              label={item.label}
              link={item.link}
            />
          ))}

          {/* Tiny spacer so the last card can center properly */}
          <div className="flex-shrink-0 w-4" />
        </div>

        {/* ─── DESKTOP (Grid: all 6 cards, with top margin) ─────────────── */}
        <div className="hidden mt-12 sm:grid grid-cols-3 md:grid-cols-6 gap-6">
          {features.map((item, idx) => (
            <FeatureCard
              key={idx}
              icon={item.icon}
              label={item.label}
              link={item.link}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryFeatures;
